import React from 'react';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { MdDownload } from 'react-icons/md';
import { addWatermarkToPdf } from '../../../../utils/pdfWatermark';

interface Session {
    id: string;
    type: 'solo' | 'team';
    goal: string;
    duration: number;
    completedAt: string;
    notes: string | string[];
    members: string[];
    xp: number;
    music?: string;
    customMusicURL?: string;
    tasks?: { task: string; isCompleted: boolean }[];
}

interface DownloadPDFProps {
    session: Session;
}

// Helper function to format duration
const formatDuration = (durationInMinutes: number): string => {
    const mins = Math.floor(durationInMinutes);
    const secs = Math.round((durationInMinutes - mins) * 60);

    if (mins === 0 && secs === 0) return '0s';
    if (mins === 0) return `${secs}s`;
    if (secs === 0) return `${mins}m`;
    return `${mins}m ${secs}s`;
};

const DownloadPDF: React.FC<DownloadPDFProps> = ({ session }) => {
    const handlePrint = async () => {
        if (!session) return;

        try {
            const doc = new jsPDF();
            const pageWidth = doc.internal.pageSize.width;
            const pageHeight = doc.internal.pageSize.height;
            const completedDate = new Date(session.completedAt);
            const formattedDate = completedDate.toLocaleDateString();
            const formattedTime = completedDate.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });

            // Brand Colors
            const brandColor: [number, number, number] = [234, 88, 12]; // Orange-600
            const brandLight: [number, number, number] = [255, 247, 237]; // Orange-50
            const slate900: [number, number, number] = [15, 23, 42];
            const slate500: [number, number, number] = [100, 116, 139];

            // Helper to match DownloadPDF style
            const addPageOverlay = (data: any) => {
                const doc = data.doc;
                const pageNumber = data.pageNumber;

                // --- Header ---
                const headerHeight = 40;
                doc.setFillColor(brandLight[0], brandLight[1], brandLight[2]);
                doc.rect(0, 0, pageWidth, headerHeight, 'F');

                doc.setDrawColor(brandColor[0], brandColor[1], brandColor[2]);
                doc.setLineWidth(1.5);
                doc.line(0, headerHeight, pageWidth, headerHeight);

                // Logo
                doc.setFontSize(24);
                doc.setFont('helvetica', 'bold');
                doc.setTextColor(brandColor[0], brandColor[1], brandColor[2]);
                doc.text('Focuzon', 14, 22);

                // Subtitle
                doc.setFontSize(10);
                doc.setFont('helvetica', 'normal');
                doc.setTextColor(slate500[0], slate500[1], slate500[2]);
                doc.text('SESSION REPORT', 14, 30);

                // --- Right Side Capsules ---
                const capsuleHeight = 7;
                const rightMargin = 14;

                const drawCapsule = (text: string, y: number) => {
                    doc.setFontSize(8);
                    doc.setFont('helvetica', 'normal');
                    const textWidth = doc.getTextWidth(text);
                    const paddingX = 4;
                    const boxWidth = textWidth + (paddingX * 2);
                    const x = pageWidth - rightMargin - boxWidth;

                    doc.setFillColor(255, 255, 255);
                    doc.setDrawColor(brandColor[0], brandColor[1], brandColor[2]);
                    doc.setLineWidth(0.1);
                    doc.roundedRect(x, y, boxWidth, capsuleHeight, 3.5, 3.5, 'FD');

                    doc.setTextColor(slate900[0], slate900[1], slate900[2]);
                    doc.text(text, x + paddingX, y + 4.5);
                    return x;
                };

                drawCapsule(`Date: ${formattedDate}`, 10);
                drawCapsule(`Time: ${formattedTime}`, 19);
                drawCapsule(`Type: ${session.type === 'solo' ? 'Solo' : 'Team'} Session`, 28);

                // --- Footer ---
                doc.setDrawColor(226, 232, 240);
                doc.setLineWidth(0.5);
                doc.line(14, pageHeight - 15, pageWidth - 14, pageHeight - 15);

                doc.setFontSize(8);
                doc.setTextColor(slate500[0], slate500[1], slate500[2]);
                doc.text(`© ${new Date().getFullYear()} Focuzon. All rights reserved.`, 14, pageHeight - 8);
                doc.text('www.focuzon.com', pageWidth / 2, pageHeight - 8, { align: 'center' });
                doc.text(`Page ${pageNumber}`, pageWidth - 14, pageHeight - 8, { align: 'right' });
            };

            // --- Content Generation ---

            // 1. Initial Page Overlay (Background)
            addPageOverlay({ doc: doc, pageNumber: 1 });

            let currentY = 55;

            // 2. Session Goal (Hero Section)
            doc.setFontSize(10);
            doc.setTextColor(slate500[0], slate500[1], slate500[2]);
            doc.text('SESSION GOAL', 14, currentY);

            currentY += 8;
            doc.setFontSize(16);
            doc.setFont('helvetica', 'bold');
            doc.setTextColor(slate900[0], slate900[1], slate900[2]);
            // Handle multi-line goal
            const splitGoal = doc.splitTextToSize(session.goal, pageWidth - 28);
            doc.text(splitGoal, 14, currentY);
            currentY += (splitGoal.length * 7) + 10;

            // Icon Drawing Helper
            const drawIcon = (type: 'clock' | 'user' | 'users' | 'mode', x: number, y: number, color: [number, number, number]) => {
                doc.setDrawColor(color[0], color[1], color[2]);
                doc.setFillColor(color[0], color[1], color[2]);
                doc.setLineWidth(0.5);

                switch (type) {
                    case 'clock':
                        // Circle
                        doc.circle(x + 3, y + 3, 2.5);
                        // Hands
                        doc.line(x + 3, y + 3, x + 3, y + 1.5);
                        doc.line(x + 3, y + 3, x + 4, y + 3);
                        break;

                    case 'user':
                        // Head
                        doc.circle(x + 3, y + 2, 1.2, 'F');
                        // Body (simple arc/triangle style)
                        doc.triangle(x + 0.5, y + 6, x + 5.5, y + 6, x + 3, y + 3.5, 'F');
                        break;

                    case 'users':
                        // Back user
                        doc.setFillColor(color[0], color[1], color[2]);
                        doc.circle(x + 4.5, y + 1.8, 1, 'F');
                        doc.triangle(x + 2.5, y + 5, x + 6.5, y + 5, x + 4.5, y + 3, 'F');
                        // Front user (white border to separate)
                        doc.setDrawColor(255, 255, 255);
                        doc.setLineWidth(0.5);
                        doc.setFillColor(color[0], color[1], color[2]);
                        doc.circle(x + 2, y + 2.5, 1.2, 'FD'); // FD = Fill and Draw
                        doc.triangle(x - 0.5, y + 6.5, x + 4.5, y + 6.5, x + 2, y + 4, 'FD');
                        break;

                    case 'mode':
                        // Simple "Tag" or Music Note
                        doc.circle(x + 2, y + 4, 1.2, 'F');
                        doc.line(x + 2.6, y + 4, x + 2.6, y + 1);
                        doc.line(x + 2.6, y + 1, x + 4.5, y + 1);
                        doc.line(x + 4.5, y + 1, x + 4.5, y + 3.5);
                        doc.circle(x + 3.9, y + 4, 1.2, 'F');
                        break;
                }
            };

            // 3. Key Stats
            const drawStat = (label: string, value: string, x: number, y: number, iconType: 'clock' | 'user' | 'users' | 'mode') => {
                // Draw Icon Background
                doc.setFillColor(brandLight[0], brandLight[1], brandLight[2]);
                doc.circle(x + 5, y + 5, 8, 'F');

                // Draw Icon
                drawIcon(iconType, x + 2, y + 2, brandColor);

                // Text Content
                const textX = x + 16;
                doc.setFontSize(8);
                doc.setFont('helvetica', 'normal');
                doc.setTextColor(slate500[0], slate500[1], slate500[2]);
                doc.text(label.toUpperCase(), textX, y + 2);

                doc.setFontSize(12);
                doc.setFont('helvetica', 'bold');
                doc.setTextColor(slate900[0], slate900[1], slate900[2]);
                doc.text(value, textX, y + 7);
            };

            drawStat('Duration', formatDuration(session.duration), 14, currentY, 'clock');
            drawStat('Participants', session.type === 'team' ? session.members.length.toString() : '1', 75, currentY, session.type === 'team' ? 'users' : 'user');

            currentY += 25;

            // 4. Tasks (if any)
            if (session.tasks && session.tasks.length > 0) {
                doc.setFontSize(11);
                doc.setFont('helvetica', 'bold');
                doc.setTextColor(brandColor[0], brandColor[1], brandColor[2]);
                doc.text('Tasks', 14, currentY);
                currentY += 5;

                const taskRows = session.tasks.map(t => [
                    t.task,
                    t.isCompleted ? 'Completed' : 'Pending'
                ]);

                autoTable(doc, {
                    startY: currentY,
                    head: [['TASK', 'STATUS']],
                    body: taskRows,
                    theme: 'grid',
                    headStyles: {
                        fillColor: brandColor as any,
                        fontSize: 9,
                        fontStyle: 'bold',
                    },
                    bodyStyles: {
                        textColor: slate900 as any,
                        fontSize: 9,
                    },
                    columnStyles: {
                        0: { cellWidth: 'auto' },
                        1: { cellWidth: 40, halign: 'center', textColor: [22, 163, 74] }
                    },
                    margin: { top: 45, bottom: 20 },
                    didDrawPage: addPageOverlay,
                });

                // Update currentY for next section
                currentY = (doc as any).lastAutoTable.finalY + 20;
            }

            // 5. Notes (if any)
            if (session.notes && (Array.isArray(session.notes) ? session.notes.length > 0 : session.notes)) {
                // Check for page break
                if (currentY > pageHeight - 50) {
                    doc.addPage();
                    addPageOverlay({ doc: doc, pageNumber: doc.getNumberOfPages() });
                    currentY = 50;
                }

                doc.setFontSize(11);
                doc.setFont('helvetica', 'bold');
                doc.setTextColor(brandColor[0], brandColor[1], brandColor[2]);
                doc.text('Session Notes', 14, currentY);
                currentY += 8;

                const notesText = Array.isArray(session.notes) ? session.notes.join('\n• ') : session.notes;
                const formattedNotes = Array.isArray(session.notes) ? '• ' + notesText : notesText;

                doc.setFontSize(10);
                doc.setFont('helvetica', 'normal');
                doc.setTextColor(slate900[0], slate900[1], slate900[2]);

                const splitNotes = doc.splitTextToSize(formattedNotes, pageWidth - 28);
                doc.text(splitNotes, 14, currentY);
                currentY += (splitNotes.length * 6) + 15;
            }

            // 6. Timeline
            if (currentY > pageHeight - 50) {
                doc.addPage();
                addPageOverlay({ doc: doc, pageNumber: doc.getNumberOfPages() });
                currentY = 50;
            }

            const timelineData = [
                ['Session Started', `${formattedDate} at ${completedDate.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`], // Simplifying start time to completed time logic roughly for now unless we have startTime
            ];
            // Note: session object in UI only shows completedAt. In real app, might want startedAt. 
            // Existing code used completedAt for both start/end timeline which implies instant session? 
            // Or just using same date. I will stick to what was available.

            if (session.completedAt) {
                timelineData.push(['Session Completed', `${formattedDate} at ${formattedTime}`]);
            }

            autoTable(doc, {
                startY: currentY,
                head: [['EVENT', 'TIME']],
                body: timelineData,
                theme: 'plain', // minimalistic for timeline
                styles: { fontSize: 9, cellPadding: 3 },
                headStyles: { fillColor: [241, 245, 249], textColor: slate500 as any },
                columnStyles: { 0: { fontStyle: 'bold', cellWidth: 50 }, 1: { cellWidth: 'auto' } },
                margin: { top: 45, bottom: 20 },
                didDrawPage: addPageOverlay,
            });

            await addWatermarkToPdf(doc);

            doc.save(`focus-session-${session.id}.pdf`);

        } catch (error) {
            console.error('Error generating PDF:', error);
        }
    };

    return (
        <button
            onClick={handlePrint}
            className="px-3 py-1.5 rounded-full text-sm font-medium flex items-center gap-1.5 bg-orange-50 text-orange-600 border border-orange-200/50 hover:bg-orange-100 transition-colors"
            title="Download as PDF"
        >
            <MdDownload className="text-base" />
            <span>PDF</span>
        </button>
    );
};

export default DownloadPDF;
